import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getTeamMeta } from '../../SnipeHub/aflTeams';
import TeamGuernsey from '../../SnipeHub/Guernsey';

// ---- Local stat helpers (mirrors AFLPropTool — kept here to stay decoupled) ----

function num(v) {
    return typeof v === 'number' && !Number.isNaN(v) ? v : null;
}

function gameId(s) {
    return s.id ?? s.match_id;
}

function roundShort(s) {
    const r = String(s.round ?? s.game?.round ?? '');
    if (!r) return '';
    return /^\d+$/.test(r) ? `R${r}` : r;
}

// Home/away for this game from the player's team vs the home club.
function venueSide(s) {
    if (!s?.game?.home_team || !s.team) return null;
    const homeAbbr = getTeamMeta(s.game.home_team).abbr;
    if (!homeAbbr) return null;
    return s.team.toUpperCase() === homeAbbr.toUpperCase() ? 'home' : 'away';
}

// Opponent's full club name (the same names guernseys are keyed on).
function resolveOpponentFull(s) {
    const home = s?.game?.home_team;
    const away = s?.game?.away_team;
    if (!home || !away) return getTeamMeta(s?.opponent).full || s?.opponent || '';

    const playerFull = getTeamMeta(s?.team).full;
    const homeFull = getTeamMeta(home).full;
    const awayFull = getTeamMeta(away).full;

    if (playerFull && playerFull === homeFull) return away;
    if (playerFull && playerFull === awayFull) return home;

    const oppFull = getTeamMeta(s?.opponent).full;
    if (oppFull && oppFull === homeFull) return home;
    if (oppFull && oppFull === awayFull) return away;
    if (oppFull) return oppFull;
    return away;
}

// Compact stats shown in each game box (header already covers season/round/opp/venue).
const BOX_STATS = [
    { field: 'disposals', label: 'Disp' },
    { field: 'kicks', label: 'Kicks' },
    { field: 'handballs', label: 'HB' },
    { field: 'marks', label: 'Marks' },
    { field: 'tackles', label: 'Tack' },
    { field: 'goals', label: 'Goals' },
    { field: 'clearances', label: 'Clr' },
    { field: 'kick_ins', label: 'KI' },
    { field: 'cba', label: 'CBA', pct: true },
    { field: 'ruck_contests', label: 'RC', pct: true },
    { field: 'tog', label: 'TOG', pct: true },
];

// The three percentage range filters available in the left panel.
const RANGE_FILTERS = [
    { key: 'cba', field: 'cba', label: 'CBA %', hint: 'Centre Bounce Attendance.' },
    { key: 'ruck_contests', field: 'ruck_contests', label: 'Ruck Contests %', hint: 'Share of ruck contests.' },
    { key: 'tog', field: 'tog', label: 'TOG %', hint: 'Time on ground.' },
];

function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Player headshot with graceful fallback to initials.
function PlayerBadge({ player, size = 34 }) {
    const [errored, setErrored] = useState(false);
    const dim = { width: size, height: size };
    if (player?.image_src && !errored) {
        return (
            <div style={dim} className="rounded-full bg-slate-800 overflow-hidden flex-shrink-0 ring-1 ring-slate-700/60">
                <img
                    src={player.image_src}
                    alt={player.player_name || ''}
                    width={size}
                    height={size}
                    onError={() => setErrored(true)}
                    className="w-full h-full object-cover object-top"
                />
            </div>
        );
    }
    return (
        <div style={dim} className="rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
            <span className="font-mono font-bold text-emerald-300/90" style={{ fontSize: size * 0.34 }}>
                {getInitials(player?.player_name)}
            </span>
        </div>
    );
}

// A reusable on/off switch matching the rest of the app.
function Switch({ enabled, onToggle, label }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            role="switch"
            aria-checked={enabled}
            aria-label={label}
            className={`relative inline-flex shrink-0 items-center rounded-full transition-colors p-0 ${
                enabled ? 'bg-emerald-500' : 'bg-slate-700'
            }`}
            style={{ width: '34px', height: '18px' }}
        >
            <span
                aria-hidden="true"
                className="block rounded-full bg-white shadow transition-transform duration-150"
                style={{ width: '14px', height: '14px', transform: enabled ? 'translateX(18px)' : 'translateX(2px)' }}
            />
        </button>
    );
}

// Dual-thumb percentage range slider. Two overlaid range inputs; only the thumbs
// receive pointer events so each handle stays independently draggable.
function RangeSlider({ min = 0, max = 100, step = 1, value, onChange }) {
    const [lo, hi] = value;
    const pct = (v) => ((v - min) / (max - min)) * 100;

    const thumbCls =
        'pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 w-full h-1.5 appearance-none bg-transparent ' +
        '[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-400 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-emerald-600 [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-pointer ' +
        '[&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-emerald-400 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-emerald-600 [&::-moz-range-thumb]:cursor-pointer';

    return (
        <div>
            <div className="flex items-baseline justify-between mb-2.5">
                <span className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Range</span>
                <span className="text-sm font-mono font-bold text-emerald-400 tabular-nums leading-none">
                    {lo}% <span className="text-slate-600">–</span> {hi}%
                </span>
            </div>
            <div className="relative h-4 flex items-center">
                <div className="absolute inset-x-0 h-1.5 rounded-full bg-slate-800" />
                <div
                    className="absolute h-1.5 rounded-full bg-emerald-500/80"
                    style={{ left: `${pct(lo)}%`, right: `${100 - pct(hi)}%` }}
                />
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={lo}
                    onChange={(e) => onChange([Math.min(Number(e.target.value), hi), hi])}
                    className={thumbCls}
                    style={{ zIndex: lo >= max - step ? 5 : 3 }}
                    aria-label="Minimum"
                />
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={hi}
                    onChange={(e) => onChange([lo, Math.max(Number(e.target.value), lo)])}
                    className={thumbCls}
                    style={{ zIndex: 4 }}
                    aria-label="Maximum"
                />
            </div>
            <div className="flex justify-between mt-1.5 text-[9px] font-mono text-slate-700 uppercase tracking-wider">
                <span>{min}%</span>
                <span>{max}%</span>
            </div>
        </div>
    );
}

// Left-panel range filter section: toggle in the header, slider when enabled.
function RangeSection({ label, hint, enabled, onToggle, range, onRange }) {
    return (
        <section
            className={`relative rounded-lg border bg-slate-900/40 px-3.5 pt-3 pb-3.5 transition-colors ${
                enabled ? 'border-emerald-500/40' : 'border-slate-800/80 hover:border-slate-700/80'
            }`}
        >
            {enabled && <span aria-hidden="true" className="absolute left-0 top-2 bottom-2 w-[2px] rounded-r bg-emerald-400" />}
            <header className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h4 className="text-[10px] font-mono font-semibold text-slate-300 uppercase tracking-[0.14em]">{label}</h4>
                    {hint && <p className="mt-0.5 text-[10px] text-slate-500 leading-snug">{hint}</p>}
                </div>
                <Switch enabled={enabled} onToggle={onToggle} label={`Toggle ${label}`} />
            </header>
            {enabled && (
                <div className="pt-3">
                    <RangeSlider value={range} onChange={onRange} />
                </div>
            )}
        </section>
    );
}

// One game card in the preview grid.
function GameBox({ stat, checked, onToggle }) {
    const [imgError, setImgError] = useState(false);
    const side = venueSide(stat);
    const oppFull = resolveOpponentFull(stat);
    const oppAbbr = getTeamMeta(oppFull).abbr || stat.opponent || '';
    const heat = stat.heatmap_url;

    return (
        <button
            type="button"
            onClick={() => onToggle(gameId(stat))}
            className={`group relative text-left rounded-xl border overflow-hidden transition-all ${
                checked
                    ? 'border-emerald-500/60 bg-emerald-500/[0.06] ring-1 ring-emerald-500/30'
                    : 'border-slate-800 bg-slate-900/50 hover:border-slate-700'
            }`}
        >
            {/* Header */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-white/[0.06]">
                <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${
                    checked ? 'bg-emerald-500 border-emerald-500' : 'border-slate-600 bg-slate-950/50'
                }`}>
                    {checked && (
                        <svg className="w-3 h-3 text-slate-950" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M5 13l4 4L19 7" />
                        </svg>
                    )}
                </span>
                <TeamGuernsey name={oppFull} className="w-5 h-5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-mono font-semibold text-white leading-none truncate">
                        {side === 'home' ? 'vs' : '@'} {oppAbbr}
                    </div>
                    <div className="text-[9px] font-mono text-slate-500 mt-0.5 truncate">
                        {stat.season} · {roundShort(stat)} · {stat.game?.venue || '—'}
                    </div>
                </div>
            </div>

            {/* Heat map */}
            <div className="relative bg-slate-950/60 h-28 flex items-center justify-center overflow-hidden">
                {heat && !imgError ? (
                    <img
                        src={heat}
                        alt="Positional heat map"
                        loading="lazy"
                        onError={() => setImgError(true)}
                        className="h-full w-full object-contain"
                    />
                ) : (
                    <div className="flex flex-col items-center gap-1 text-slate-700">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 12a9 9 0 1018 0 9 9 0 00-18 0zm9-9v18m-9-9h18" />
                        </svg>
                        <span className="text-[9px] font-mono uppercase tracking-wider">No heat map</span>
                    </div>
                )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-px bg-white/[0.04]">
                {BOX_STATS.map(({ field, label, pct }) => {
                    const v = num(stat[field]);
                    return (
                        <div key={field} className="bg-slate-900/70 px-1.5 py-1.5 text-center">
                            <div className="text-[8px] font-mono uppercase tracking-wider text-slate-600 leading-none">{label}</div>
                            <div className="text-[12px] font-mono font-bold tabular-nums text-slate-200 leading-none mt-1">
                                {v == null ? '—' : pct ? `${Math.round(v)}%` : v}
                            </div>
                        </div>
                    );
                })}
            </div>
        </button>
    );
}

function CustomPropDataFilterModal({ stats, player, seasonList, initialSelectedIds, initialSettings, onConfirm, onClose }) {
    const allStats = useMemo(() => stats || [], [stats]);

    // Restore the saved season selection if reopening, else default to all seasons
    // so the preview shows the full history up front.
    const [seasons, setSeasons] = useState(() => {
        if (initialSettings?.seasons) return initialSettings.seasons.slice();
        return seasonList && seasonList.length
            ? seasonList.slice()
            : Array.from(new Set(allStats.map((s) => s.season))).filter((v) => v != null);
    });

    // Restore saved range filters if reopening, else start with everything off.
    const [ranges, setRanges] = useState(() => {
        const def = {
            cba: { enabled: false, range: [0, 100] },
            ruck_contests: { enabled: false, range: [0, 100] },
            tog: { enabled: false, range: [0, 100] },
        };
        if (!initialSettings?.ranges) return def;
        return {
            cba: { ...def.cba, ...initialSettings.ranges.cba, range: (initialSettings.ranges.cba?.range || def.cba.range).slice() },
            ruck_contests: { ...def.ruck_contests, ...initialSettings.ranges.ruck_contests, range: (initialSettings.ranges.ruck_contests?.range || def.ruck_contests.range).slice() },
            tog: { ...def.tog, ...initialSettings.ranges.tog, range: (initialSettings.ranges.tog?.range || def.tog.range).slice() },
        };
    });

    // Preview = games within the selected seasons, most recent first.
    const previewGames = useMemo(() => {
        const list = allStats.filter((s) => seasons.includes(s.season));
        return list.slice().reverse();
    }, [allStats, seasons]);

    // Group the preview by season (years are contiguous since the list is
    // sorted) so each year can be shown as a collapsible section.
    const previewGroups = useMemo(() => {
        const map = new Map();
        for (const s of previewGames) {
            const key = s.season ?? 'Unknown';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(s);
        }
        return Array.from(map.entries()).map(([season, games]) => ({ season, games }));
    }, [previewGames]);

    // Mobile only: the filters panel collapses behind a toggle so the game
    // preview is front-and-centre. No effect at lg+ (it's always a side panel).
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    const [collapsedSeasons, setCollapsedSeasons] = useState(() => new Set());
    const toggleCollapse = (season) => {
        setCollapsedSeasons((prev) => {
            const next = new Set(prev);
            if (next.has(season)) next.delete(season); else next.add(season);
            return next;
        });
    };

    // Does a game pass every enabled percentage range? Null values fail an active filter.
    const passesRanges = useMemo(() => {
        return (s) => {
            for (const rf of RANGE_FILTERS) {
                const cfg = ranges[rf.key];
                if (!cfg?.enabled) continue;
                const v = num(s[rf.field]);
                if (v == null) return false;
                if (v < cfg.range[0] || v > cfg.range[1]) return false;
            }
            return true;
        };
    }, [ranges]);

    // Selected game ids. Seed from a prior custom set if reopening, else everything.
    const [checked, setChecked] = useState(() => {
        if (initialSelectedIds && initialSelectedIds.length) return new Set(initialSelectedIds);
        return new Set(allStats.map(gameId));
    });

    // Auto check/uncheck the preview whenever the season or range filters change.
    // Skipped on the first render so a restored selection isn't clobbered.
    const didInit = useRef(false);
    const seasonsKey = seasons.slice().sort((a, b) => a - b).join(',');
    const rangesKey = RANGE_FILTERS
        .map((rf) => `${rf.key}:${ranges[rf.key].enabled ? `${ranges[rf.key].range[0]}-${ranges[rf.key].range[1]}` : 'off'}`)
        .join('|');

    useEffect(() => {
        // On first render, keep a restored selection intact (it already reflects
        // the saved settings plus any manual tweaks). With no saved selection,
        // fall through so the restored range filters are applied immediately.
        if (!didInit.current) {
            didInit.current = true;
            if (initialSelectedIds && initialSelectedIds.length) return;
        }
        const next = new Set();
        for (const s of previewGames) {
            if (passesRanges(s)) next.add(gameId(s));
        }
        setChecked(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seasonsKey, rangesKey]);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', onKey);
        };
    }, [onClose]);

    const toggleSeason = (s) => {
        setSeasons((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
    };

    const toggleRange = (key) => {
        setRanges((prev) => ({ ...prev, [key]: { ...prev[key], enabled: !prev[key].enabled } }));
    };
    const setRangeValue = (key, range) => {
        setRanges((prev) => ({ ...prev, [key]: { ...prev[key], range } }));
    };

    const toggleGame = (id) => {
        setChecked((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    // Only count/confirm games actually visible in the current preview.
    const previewIds = useMemo(() => previewGames.map(gameId), [previewGames]);
    const selectedCount = useMemo(
        () => previewIds.reduce((acc, id) => acc + (checked.has(id) ? 1 : 0), 0),
        [previewIds, checked]
    );
    const allSelected = previewIds.length > 0 && selectedCount === previewIds.length;

    const selectAll = () => setChecked(new Set(previewIds));
    const selectNone = () => setChecked(new Set());

    const handleConfirm = () => {
        const ids = previewIds.filter((id) => checked.has(id));
        onConfirm(ids, { seasons: seasons.slice(), ranges });
    };

    const seasonChips = (seasonList && seasonList.length
        ? seasonList
        : Array.from(new Set(allStats.map((s) => s.season))).filter((v) => v != null).sort((a, b) => b - a));

    const enabledRangeCount = RANGE_FILTERS.filter((rf) => ranges[rf.key].enabled).length;

    return createPortal(
        <div
            className="fixed inset-0 flex items-end md:items-center justify-center p-0 md:p-6 bg-slate-950/80 backdrop-blur-sm animate-fade-in"
            style={{ zIndex: 10000 }}
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="Custom Dataset Filter"
        >
            <div
                className="relative w-full md:max-w-6xl lg:max-w-7xl bg-slate-900 border-t md:border border-slate-800 rounded-t-2xl md:rounded-2xl shadow-2xl shadow-black/60 overflow-hidden flex flex-col max-h-[92vh] animate-slide-up md:animate-fade-in"
                onClick={(e) => e.stopPropagation()}
                style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 0px)' }}
            >
                <div aria-hidden="true" className="md:hidden flex justify-center pt-2">
                    <span className="block w-10 h-1 rounded-full bg-slate-700/80" />
                </div>
                <div className="hidden md:block absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/60 to-transparent" />

                {/* Header */}
                <div className="relative flex items-center justify-between px-5 pt-3 md:pt-4 pb-3.5 border-b border-slate-800/80">
                    <div className="flex items-center gap-3 min-w-0">
                        <PlayerBadge player={player} size={34} />
                        <div className="min-w-0">
                            <h3 className="text-sm font-semibold text-white tracking-tight truncate">
                                {player?.player_name ? `${player.player_name} · ` : ''}Custom Dataset
                            </h3>
                            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-[0.14em]">
                                Create your own dataset
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="w-8 h-8 rounded-md flex items-center justify-center text-slate-500 hover:text-white hover:bg-slate-800/70 transition-colors flex-shrink-0"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
                        </svg>
                    </button>
                </div>

                {/* Body: filters | preview */}
                <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
                    {/* Section 1 — filters. Collapsible on mobile so the game
                        preview gets the screen; always a side panel at lg+. */}
                    <div className="lg:w-80 flex-shrink-0 border-b lg:border-b-0 lg:border-r border-slate-800/80 flex flex-col min-h-0 max-h-[50vh] lg:max-h-none">
                        <button
                            type="button"
                            onClick={() => setMobileFiltersOpen((v) => !v)}
                            aria-expanded={mobileFiltersOpen}
                            className="lg:hidden flex items-center justify-between gap-2 px-4 py-3 flex-shrink-0 bg-slate-950/30"
                        >
                            <span className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h18M6 12h12M10 17h4" />
                                </svg>
                                <span className="text-[11px] font-mono uppercase tracking-[0.16em] text-slate-300">Filters</span>
                                {enabledRangeCount > 0 && (
                                    <span className="min-w-[16px] h-4 px-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[9px] font-mono font-bold leading-none inline-flex items-center justify-center tabular-nums">
                                        {enabledRangeCount}
                                    </span>
                                )}
                            </span>
                            <span className="flex items-center gap-2">
                                <span className="text-[10px] font-mono text-slate-500 tabular-nums">{seasons.length}/{seasonChips.length} seasons</span>
                                <svg className={`w-4 h-4 text-slate-400 transition-transform ${mobileFiltersOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </span>
                        </button>
                        <div className={`${mobileFiltersOpen ? 'block' : 'hidden'} lg:block flex-1 min-h-0 overflow-y-auto styled-scrollbar px-4 pb-4 pt-0 lg:pt-4 space-y-4`}>
                        <div>
                            <div className="flex items-center gap-2.5 mb-2.5">
                                <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">Seasons</span>
                                <span className="h-px flex-1 bg-slate-800/70" />
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {seasonChips.map((s) => {
                                    const active = seasons.includes(s);
                                    return (
                                        <button
                                            key={s}
                                            type="button"
                                            onClick={() => toggleSeason(s)}
                                            className={`px-3 py-1.5 rounded-lg text-[11px] font-mono font-semibold border transition-all ${
                                                active
                                                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                                                    : 'bg-slate-900/50 text-slate-400 border-slate-700/60 hover:text-slate-200 hover:border-slate-600'
                                            }`}
                                        >
                                            {s}
                                        </button>
                                    );
                                })}
                            </div>
                            {!seasonChips.length && (
                                <p className="text-[11px] font-mono text-slate-600">No seasons on record</p>
                            )}
                        </div>

                        <div>
                            <div className="flex items-center gap-2.5 mb-2.5">
                                <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">Advanced</span>
                                <span className="h-px flex-1 bg-slate-800/70" />
                            </div>
                            <div className="space-y-2.5">
                                {RANGE_FILTERS.map((rf) => (
                                    <RangeSection
                                        key={rf.key}
                                        label={rf.label}
                                        hint={rf.hint}
                                        enabled={ranges[rf.key].enabled}
                                        onToggle={() => toggleRange(rf.key)}
                                        range={ranges[rf.key].range}
                                        onRange={(r) => setRangeValue(rf.key, r)}
                                    />
                                ))}
                            </div>
                            <p className="mt-3 text-[10px] font-mono text-slate-600 leading-relaxed">
                                Enabling a range auto-selects games within it. You can still tick / untick individual games on the right.
                            </p>
                        </div>
                        </div>
                    </div>

                    {/* Section 2 — preview */}
                    <div className="flex-1 min-w-0 flex flex-col min-h-0">
                        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-800/80 bg-slate-950/30">
                            <div className="flex items-baseline gap-2">
                                <span className="text-sm font-mono font-bold text-emerald-300 tabular-nums">{selectedCount}</span>
                                <span className="text-[11px] font-mono text-slate-500">
                                    of {previewIds.length} games selected
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <button
                                    type="button"
                                    onClick={selectAll}
                                    disabled={allSelected || !previewIds.length}
                                    className="px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider text-slate-400 border border-slate-700/60 hover:text-emerald-300 hover:border-emerald-500/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    All
                                </button>
                                <button
                                    type="button"
                                    onClick={selectNone}
                                    disabled={!selectedCount}
                                    className="px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider text-slate-400 border border-slate-700/60 hover:text-rose-300 hover:border-rose-500/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    None
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto styled-scrollbar p-4">
                            {previewGames.length ? (
                                <div className="space-y-3">
                                    {previewGroups.map(({ season, games }) => {
                                        const isCollapsed = collapsedSeasons.has(season);
                                        const sel = games.reduce((acc, g) => acc + (checked.has(gameId(g)) ? 1 : 0), 0);
                                        return (
                                            <section key={season}>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleCollapse(season)}
                                                    className="w-full flex items-center gap-2.5 px-1 py-1.5 mb-2 group"
                                                >
                                                    <svg
                                                        className={`w-3.5 h-3.5 text-slate-500 group-hover:text-slate-300 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                    <span className="text-[12px] font-mono font-bold text-slate-200 tracking-wide">{season}</span>
                                                    <span className="h-px flex-1 bg-slate-800/70" />
                                                    <span className="text-[10px] font-mono text-slate-500 tabular-nums">
                                                        <span className="text-emerald-300 font-bold">{sel}</span>/{games.length}
                                                    </span>
                                                </button>
                                                {!isCollapsed && (
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                                                        {games.map((s) => (
                                                            <GameBox
                                                                key={gameId(s)}
                                                                stat={s}
                                                                checked={checked.has(gameId(s))}
                                                                onToggle={toggleGame}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </section>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center text-center py-16">
                                    <div className="w-12 h-12 rounded-xl bg-slate-800/50 border border-slate-700/50 flex items-center justify-center mb-4">
                                        <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 6h16M4 12h16M4 18h7" />
                                        </svg>
                                    </div>
                                    <p className="text-sm text-slate-300 font-medium">No games to show</p>
                                    <p className="text-[12px] font-mono text-slate-500 mt-1">Select at least one season to populate the preview.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="relative flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-800/80 bg-slate-950/40">
                    <span className="text-[11px] font-mono text-slate-500">
                        <span className="text-emerald-300 font-bold tabular-nums">{selectedCount}</span> games in dataset
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 md:py-1.5 text-[11px] md:text-xs font-medium text-slate-300 rounded-md border border-slate-700/80 hover:bg-slate-800/60 hover:border-slate-600 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirm}
                            disabled={!selectedCount}
                            className="px-4 py-2 md:py-1.5 text-[11px] md:text-xs font-semibold text-emerald-950 bg-emerald-400 rounded-md hover:bg-emerald-300 transition-colors shadow-[0_0_0_1px_rgba(16,185,129,0.4)] disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Confirm Dataset{selectedCount ? ` (${selectedCount})` : ''}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default CustomPropDataFilterModal;
